import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GameState } from '../../../shared/types';
import { BallPhysics, BallPhysicsConfig } from '../utils/ballPhysics';

interface PitchData {
  player1: { pitch: number; timestamp: number } | null;
  player2: { pitch: number; timestamp: number } | null;
}

interface UseGameStateReturn {
  gameState: GameState;
  updateGameState: (newState: Partial<GameState>) => void;
  updatePitchData: (pitchData: PitchData) => void;
  handleCountdown: (seconds: number) => void;
  handleGameStart: () => void;
  handleGameEnd: (winner: 1 | 2, finalScore: { player1: number; player2: number }) => void;
  handleGamePaused: (waitingForPlayer: 1 | 2) => void;
  handleGameResumed: () => void;
  startRenderLoop: () => void;
  stopRenderLoop: () => void;
  sendScoreUpdate?: (score: { player1: number; player2: number }) => void;
}

export function useGameState(gameId: string, sendScoreUpdate?: (score: { player1: number; player2: number }) => void): UseGameStateReturn {
  const [gameState, setGameState] = useState<GameState>(() => createInitialGameState(gameId));
  const renderLoopRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(Date.now());

  // Simple pitch data storage - no complex controllers
  const pitchDataRef = useRef<PitchData>({ player1: null, player2: null });

  // Track if we're starting a new game (vs resuming from pause)
  const isNewGameRef = useRef<boolean>(true);

  // Store sendScoreUpdate function in a ref to avoid render loop restarts
  const sendScoreUpdateRef = useRef(sendScoreUpdate);
  sendScoreUpdateRef.current = sendScoreUpdate;

  // Track last pitch for delta-based movement
  const player1RangeRef = useRef({ lastPitch: null as number | null });
  const player2RangeRef = useRef({ lastPitch: null as number | null });

  // Ball physics configuration - memoized to prevent re-creation on every render
  const ballPhysicsConfig: BallPhysicsConfig = useMemo(() => ({
    ballSpeed: 150, // Base speed in pixels per second (reasonable for human play)
    ballSize: 10, // Ball size in pixels (matches BALL_SIZE in GameDisplay)
    paddleWidth: 10, // Paddle width in pixels
    paddleHeight: 80, // Paddle height in pixels
    canvasWidth: 800, // Canvas width in pixels
    canvasHeight: 400, // Canvas height in pixels
    speedIncrement: 0.08 // 8% speed increase per paddle hit (more gradual)
  }), []);

  const ballPhysicsRef = useRef<BallPhysics>(new BallPhysics(ballPhysicsConfig));

  const updateGameState = useCallback((newState: Partial<GameState>) => {
    setGameState(prevState => ({
      ...prevState,
      ...newState
    }));
  }, []);

  const updatePitchData = useCallback((pitchData: PitchData) => {
    pitchDataRef.current = pitchData;
  }, []);

  const handleCountdown = useCallback((seconds: number) => {
    setGameState(prevState => {
      const newState = {
        ...prevState,
        gameStatus: 'countdown' as const,
        countdown: seconds
      };

      // Reset game state immediately when countdown starts for a new game
      // New game: coming from 'waiting' (first game) or 'ended' (new game after previous ended)
      // Resume from pause: coming from 'paused'
      if (prevState.gameStatus === 'waiting' || prevState.gameStatus === 'ended') {
        console.log('Resetting game state for new game during countdown (was:', prevState.gameStatus, ')');
        // This is a new game - reset score and initialize ball
        newState.score = {
          player1: 0,
          player2: 0
        };
        BallPhysics.initializeBall(newState, ballPhysicsConfig);
        isNewGameRef.current = true;
      } else {
        console.log('NOT resetting game state during countdown (was:', prevState.gameStatus, ')');
        isNewGameRef.current = false;
      }

      return newState;
    });
  }, [ballPhysicsConfig]);

  const handleGameStart = useCallback(() => {
    setGameState(prevState => ({
      ...prevState,
      gameStatus: 'playing' as const,
      countdown: 0
    }));
  }, []);

  const handleGameEnd = useCallback((_winner: 1 | 2, finalScore: { player1: number; player2: number }) => {
    setGameState(prevState => ({
      ...prevState,
      gameStatus: 'ended',
      score: finalScore
    }));
  }, []);

  const handleGamePaused = useCallback((waitingForPlayer: 1 | 2) => {
    setGameState(prevState => ({
      ...prevState,
      gameStatus: 'paused',
      pausedWaitingFor: waitingForPlayer
    }));
  }, []);

  const handleGameResumed = useCallback(() => {
    setGameState(prevState => {
      // Set the flag to indicate this is NOT a new game (resuming from pause)
      isNewGameRef.current = false;

      return {
        ...prevState,
        gameStatus: 'countdown', // Will transition to playing after countdown
        pausedWaitingFor: undefined
      };
    });
  }, []);

  const startRenderLoop = useCallback(() => {
    if (renderLoopRef.current) return; // Already running

    const gameLoop = () => {
      const now = Date.now();
      const deltaTime = (now - lastUpdateTimeRef.current) / 1000; // Convert to seconds
      lastUpdateTimeRef.current = now;

      setGameState(prevState => {
        const newState = { ...prevState };

        // Countdown is handled by server messages, no need to decrement here
        // The server sends countdown updates and gameStart message

        // Delta-based paddle control - movement based on pitch change
        const currentPitchData = pitchDataRef.current;

        // Maximum paddle movement per frame (pixels) - prevents erratic jumps from noise
        const maxMovementPerFrame = 12; // Adjust this value to control movement rate limiting

        // Player 1 paddle - delta-based movement with rate limiting
        if (currentPitchData.player1) {
          const currentPitch = currentPitchData.player1.pitch;
          const lastPitch = player1RangeRef.current.lastPitch;

          if (lastPitch !== null) {
            // Calculate pitch delta (difference from last pitch)
            const pitchDelta = currentPitch - lastPitch;

            // Convert pitch delta to paddle movement in pixels
            // Sensitivity: how many pixels paddle moves per Hz of pitch change
            const sensitivity = 3; // pixels movement per Hz
            let paddleMovement = -pitchDelta * sensitivity; // Negative: higher pitch = move up

            // Rate limiting: clamp movement to maximum allowed per frame
            paddleMovement = Math.max(-maxMovementPerFrame, Math.min(maxMovementPerFrame, paddleMovement));

            // Apply movement to current paddle position (in pixels)
            const maxY = ballPhysicsConfig.canvasHeight - ballPhysicsConfig.paddleHeight;
            const newPosition = newState.paddles.player1.y + paddleMovement;
            newState.paddles.player1.y = Math.max(0, Math.min(maxY, newPosition));
          }

          // Store current pitch as last pitch for next frame
          player1RangeRef.current.lastPitch = currentPitch;
        }

        // Player 2 paddle - same delta logic with rate limiting
        if (currentPitchData.player2) {
          const currentPitch = currentPitchData.player2.pitch;
          const lastPitch = player2RangeRef.current.lastPitch;

          if (lastPitch !== null) {
            // Calculate pitch delta (difference from last pitch)
            const pitchDelta = currentPitch - lastPitch;

            // Convert pitch delta to paddle movement in pixels
            const sensitivity = 2; // 2 pixels movement per Hz
            let paddleMovement = -pitchDelta * sensitivity; // Negative: higher pitch = move up

            // Rate limiting: clamp movement to maximum allowed per frame
            paddleMovement = Math.max(-maxMovementPerFrame, Math.min(maxMovementPerFrame, paddleMovement));

            // Apply movement to current paddle position (in pixels)
            const maxY = ballPhysicsConfig.canvasHeight - ballPhysicsConfig.paddleHeight;
            const newPosition = newState.paddles.player2.y + paddleMovement;
            newState.paddles.player2.y = Math.max(0, Math.min(maxY, newPosition));
          }

          // Store current pitch as last pitch for next frame
          player2RangeRef.current.lastPitch = currentPitch;
        }

        // Ball physics and collision detection
        if (newState.gameStatus === 'playing') {
          const collisionResult = ballPhysicsRef.current.updateBall(newState, deltaTime);

          // Handle scoring
          if (collisionResult.scored && collisionResult.scoringPlayer) {
            // Player scored

            // Send score update to server
            if (sendScoreUpdateRef.current) {
              sendScoreUpdateRef.current(newState.score);
            }

            // Check for game end condition (first to 5 points wins)
            const targetScore = 5;
            if (newState.score.player1 >= targetScore || newState.score.player2 >= targetScore) {
              newState.gameStatus = 'ended';
            }
          }
        }

        return newState;
      });

      // Continue the loop
      renderLoopRef.current = requestAnimationFrame(gameLoop);
    };

    lastUpdateTimeRef.current = Date.now();
    renderLoopRef.current = requestAnimationFrame(gameLoop);
  }, [ballPhysicsConfig.canvasHeight, ballPhysicsConfig.paddleHeight]);

  const stopRenderLoop = useCallback(() => {
    if (renderLoopRef.current) {
      cancelAnimationFrame(renderLoopRef.current);
      renderLoopRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Start render loop when component mounts
    startRenderLoop();

    return () => {
      stopRenderLoop();
    };
  }, [startRenderLoop, stopRenderLoop]);

  return {
    gameState,
    updateGameState,
    updatePitchData,
    handleCountdown,
    handleGameStart,
    handleGameEnd,
    handleGamePaused,
    handleGameResumed,
    startRenderLoop,
    stopRenderLoop,
    sendScoreUpdate
  };
}

function createInitialGameState(gameId: string): GameState {
  const canvasWidth = 800;
  const canvasHeight = 400;
  const paddleHeight = 80;

  return {
    gameId,
    ball: {
      x: canvasWidth / 2, // Center of screen (pixels)
      y: canvasHeight / 2,
      vx: 120, // Moving right at 120 pixels/second (playable speed)
      vy: 60 // Moving slightly up at 60 pixels/second
    },
    paddles: {
      player1: {
        y: (canvasHeight - paddleHeight) / 2, // Center position (pixels)
        targetY: (canvasHeight - paddleHeight) / 2
      },
      player2: {
        y: (canvasHeight - paddleHeight) / 2,
        targetY: (canvasHeight - paddleHeight) / 2
      }
    },
    score: {
      player1: 0,
      player2: 0
    },
    gameStatus: 'waiting',
    countdown: 0,
    players: {
      player1: false,
      player2: false
    }
  };
}